import React, { useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Chart } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

/**
 * ChartDisplay Component
 * Renders charts using Chart.js based on provided configuration
 */
const ChartDisplay = ({ config, metadata }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    // Cleanup chart on unmount
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, []);

  if (!config || !config.type || !config.data) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <p className="font-medium">Invalid Chart Configuration</p>
        <p className="text-sm mt-1">The chart configuration is missing or invalid.</p>
      </div>
    );
  }

  return (
    <div className="my-4">
      {/* Chart Container */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        {/* Chart Title */}
        {metadata?.title && (
          <div className="mb-3 pb-3 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">
              {metadata.title}
            </h3>
            {metadata.subtitle && (
              <p className="text-sm text-gray-500 mt-1">{metadata.subtitle}</p>
            )}
          </div>
        )}

        {/* Chart Canvas */}
        <div className="relative" style={{ height: '400px' }}>
          <Chart
            ref={chartRef}
            type={config.type}
            data={config.data}
            options={{
              ...config.options,
              responsive: true,
              maintainAspectRatio: false
            }}
          />
        </div>

        {/* Chart Footer/Metadata */}
        {metadata?.generatedAt && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <p className="text-xs text-gray-400">
              Generated: {new Date(metadata.generatedAt).toLocaleString()}
            </p>
          </div>
        )}
      </div>

      {/* Chart Actions */}
      <div className="mt-2 flex gap-2 text-xs">
        <button
          onClick={() => {
            if (chartRef.current) {
              const url = chartRef.current.toBase64Image();
              const link = document.createElement('a');
              link.download = `chart-${Date.now()}.png`;
              link.href = url;
              link.click();
            }
          }}
          className="text-blue-600 hover:text-blue-700 font-medium"
        >
          Download Chart
        </button>
      </div>
    </div>
  );
};

export default ChartDisplay;
